import { Subscription } from 'rxjs';
import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewChild, Directive, ViewContainerRef, ElementRef, ComponentRef, AfterViewInit, AfterViewChecked, Type, OnChanges, NgZone, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormArray, FormControl, FormArrayName, FormBuilder, AbstractControl } from '@angular/forms';
import { HttpEventType } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { FileUploadService } from '../../shared/core/file-upload.service';
import { AssetViewerComponent } from './asset-viewer.component';
import { ClearanceItemService } from '../clearance-item.service';
import { CIAsset, CIImage, CIDocument, ClearanceItem, AssetId } from '../clearance-item.model';
import { ModalService } from '../../shared/helper-components/modal/_services/modal.service';
import { IAssetProcessing } from "./asset-processing.interface";
import { AssetImageProcessing } from "./asset-image-processing.service";
import { AssetDocumentProcessing } from "./asset-document-processing.service";
import { saveAs } from 'file-saver';
import { NotificationsService, Notification } from '../../shared/helper-components/index';
import { AppService } from '../../app.service';
import { ModalPromptService } from '../../shared/helper-components/modal-prompt.service';
import { AppUtils } from '../../shared/utils';
import { CarouselComponent } from '../carousel.component';
import { first } from 'rxjs/internal/operators/first';

@Component({
    selector: 'clearance-asset-manager',
    standalone: false,
    templateUrl: 'clearance-item-asset-manager.component.html',
    styleUrls: ['clearance-item-asset-manager.component.css'],
    providers: [
        AssetImageProcessing,
        AssetDocumentProcessing
    ]
})

export class ClearanceItemAssetManagerComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild('assetChooser', { static: false }) input: ElementRef;
    @ViewChild('largeImageElement', { static: false }) img: ElementRef;

    @Input('assetType')
    public assetType: string;
    @Input('asset')
    public asset: string;
    @Input('assetLabel')
    public assetLabel: string;
    @Input('group')
    public assetItemForm: FormGroup;
    @Input('assetsArray')
    public incomingAssetsArray: FormArray = new FormArray([]);
    @Input('assetsArrayName')
    public assetsArrayName: string;
    @Input('projectId')
    public projectId: string;
    @Input('images')
    public images: CIImage[] = [];
    @Input('assets')
    public assets: CIAsset[] = [];
    @Input('documents')
    public documents: CIDocument[] = [];
    @Input('hideReportDefaultIcons')
    public hideReportDefaultIcons: boolean;
    @Input('ownerType')
    public ownerType: string;
    @Output() defaultAssetChanged = new EventEmitter<FormGroup>()
    @Input('isClearanceUser')
    public isClearanceUser: boolean;
    @Input('itemStatus')
    public itemStatus: string;
    @Input('statusComments')
    public statusComments: string;
    @Output() itemDeleted: EventEmitter <object> = new EventEmitter<object>();
    @Input('showOpenInNew')
    public showOpenInNew: boolean = false;
    @Input('readOnly')
    public readOnly: boolean = false;
    @Input('projectDepartment')
    public projectDepartment: string;
    @Input('currentCI')
    public currentCI: ClearanceItem;
    @ViewChild('carousel', { static: false }) carousel: CarouselComponent;
    @Output() onCIStatusChanged: EventEmitter<any> = new EventEmitter();

    public assetsArray: FormArray;
    public largeImage: SafeUrl;
    public largeImageName: string;
    public dataImages = [];

    _assetProcessing: Map<string, IAssetProcessing> = new Map<string, IAssetProcessing>();
    showSpinner: boolean = false;
    supportedImages: string[] = ['png', 'jpeg', 'jpg', 'bmp'];
    imageModalId: string = "image-modal-";
    promptSubscription: Subscription;

    assetsMetaData: { [type: string]: AssetId } = {
        image: { icon: 'add_a_photo', label: 'Choose an Image' },
        reference: { icon: 'description', label: 'Choose a Reference Image' },
        document: { icon: 'attach_file', label: 'Choose a Release Form' }
    };

    private imageDownloadStartedSub: Subscription;
    private imageDownloadCompletedSub: Subscription;
    private documentDownloadCompletedSub: Subscription;
    currentAssetOnDisplay: FormGroup;
    assetToDownload: FormGroup;
    defaultAsset: FormGroup;

    carouselImages = [];
    showCarousel: boolean = false;
    currentIndex: Number = 0;

    constructor(
        public sanitizer: DomSanitizer,
        private modalService: ModalService,
        private _imageProcessing: AssetImageProcessing,
        private _documentProcessing: AssetDocumentProcessing,
        private fb: FormBuilder,
        private _notificationsService: NotificationsService,
        private _appService: AppService,
        private _prompt: ModalPromptService,
        private _fileUploadService: FileUploadService,
        private _ngZone: NgZone,
        private _cdr: ChangeDetectorRef
    ) {
        this._assetProcessing.set('image', _imageProcessing);
        this._assetProcessing.set('document', _documentProcessing);
        this.modalService.subscribeToModalOpenClose().subscribe(
            action => {
                if (!action) { //closing
                    this.imageIsLoaded = false;
                }
            });
        this.currentAssetOnDisplay = this.initAsset(new CIAsset());
    }
    imageIsLoaded: Boolean = false;

    setImageLoaded() {
        this.imageIsLoaded = true;
        this.showSpinner = false;
    }

    ngOnInit(): void {
        if (!this.projectId)
            this.projectId = 'Generic';

        this.initItemForm(this.images ? this.images : [], false);
        this.initUploader();
        this.defaultAsset = this.assetsArray.controls.find(c=> c.get('defaultC').value) as FormGroup;
    }
    ngOnDestroy(): void {
        if (this.assetType === 'image') {
            if (this.imageDownloadStartedSub)
                this.imageDownloadStartedSub.unsubscribe();
            if (this.imageDownloadCompletedSub)
                this.imageDownloadCompletedSub.unsubscribe();
        }

        if (this.assetType === 'document') {
            if (this.documentDownloadCompletedSub)
                this.documentDownloadCompletedSub.unsubscribe();
        }
        if (this.promptSubscription && !this.promptSubscription.closed)
            this.promptSubscription.unsubscribe();
    }
    initItemForm(images: CIAsset[] = [], refeshForm: boolean): void {

        if (!this.assetItemForm || refeshForm) {
            this.assetItemForm = this.fb.group({
                assets: this.fb.array(this.initAssets()),
                status: ''
            });
            this.assetsArray = this.assetItemForm.controls.assets as FormArray;
        } else {
            //console.log(JSON.stringify(this.incomingAssetsArray.value))
            this.assetsArray = this.incomingAssetsArray;
        }
    }

    initAssets(): FormGroup[] {

        // console.log('ClearanceItemAssetManager.initimages() >> ' + assetType + ' with images: ' + JSON.stringify(this.images))
        var groups = [];
        if (this.assets)
            for (let rest of this.assets) {
                groups.push(this.initAsset(rest));
            }
        return groups;

    }

    initAsset(asset: CIAsset): FormGroup {
        return this.fb.group({
            id: asset.id,
            name: asset.name,
            urlName: asset.urlName,
            url: asset.url,
            fullUrl: asset.fullUrl,
            fileType: asset.fileType,
            cIId: asset.cIId,
            defaultC: asset.defaultC,
            showOnReports: asset.showOnReports,
            loading: asset.loading,
            savedId: asset.savedId,
            downloading: asset.downloading,
            assetType: asset.assetType,
            originalImageFullUrl: asset.originalImageFullUrl,
            showOpenInNew: this.showOpenInNew,
            highlighted: asset.highlighted,
            font: asset.font,
            usage: asset.usage,
            tag: asset.tag,
            notes: asset.notes,
            mediumImageFullUrl: asset.mediumImageFullUrl,
            relatedImages: this.fb.array(
                asset.relatedImages || []
            ),
            comments: asset.comments
        });

    }

    ngAfterViewInit(): void {
        // console.log('incomingAssetsArray: ' + JSON.stringify(this.incomingAssetsArray.value) + ' for: ' + this.assetsArrayName);
        if (this.assetType === 'image') {

            this.imageDownloadStartedSub = this._imageProcessing.onDownloadStarted.subscribe(directDownload => {
                this.largeImage = '';
                this.largeImageName = '';
                if (!directDownload){
                    this.openModal(this.imageModalId + this.asset);
                    this.showSpinner = true;    
                }
            });

            this.imageDownloadCompletedSub = this._imageProcessing.onDownloadFinished.subscribe(item => {
                if (this.ImageIsNotSupported(item.asset.fullUrl)) {
                    this.showSpinner = false;
                    this.imageIsLoaded = false;
                    if (item.direct){
                        this._appService.getFileAs(item.asset).subscribe({
                            next: f => {
                                saveAs(f, item.asset.name);
                                this._ngZone.run(() => this.downloadComplete(item.asset.id));
                            },
                            error: () => {
                                this._ngZone.run(() => this.downloadComplete(item.asset.id));
                            }
                        });
                    }
                    return;
                }

                this.largeImage = item.asset.fullUrl;
                this.largeImageName = item.asset.name;

                //if this was to download (vs display on the screen), save the stream to the file system
                if (item.direct){
                    this._appService.getFileAs(item.asset).subscribe({
                        next: f => {
                            saveAs(f, item.asset.name);
                            this._ngZone.run(() => this.downloadComplete(item.asset.id));
                        },
                        error: () => {
                            this._ngZone.run(() => this.downloadComplete(item.asset.id));
                        }
                    });
                }
            });
        }

        if (this.assetType === 'document') {
            this.documentDownloadCompletedSub = this._documentProcessing.onDownloadFinished.subscribe(item => {
                window.open(item.asset.fullUrl, '_blank');
            })
        }
    }

    downloadComplete(id: string){
        let assetForm = this.assetsArray.controls.find(c=> c.get('id').value == id) as FormGroup;
        if (assetForm){
            const downloadingControl = assetForm.get('downloading');
            if (downloadingControl) {
                downloadingControl.patchValue(false);
            } else {
                console.warn('ClearanceItemAssetManagerComponent.downloadComplete() >> missing downloading control for asset id:', id);
            }
        }            
    }

    ImageIsNotSupported(url: string): Boolean {
        var uri = new URL(url);
        var urlTokens = uri.pathname.split('.');
        var fileType = '';

        if (urlTokens.length > 0) {
            fileType = urlTokens[urlTokens.length - 1].split('?')[0];
        }

        return !this.supportedImages.find(f => f.toUpperCase() === fileType.toUpperCase());
    }

    fileIsOver: boolean = false;

    fileOverBase(e: any): void {
        // console.log('file is over');
        this.fileIsOver = e;
    }

    openModal(id: string) {
        this.modalService.open(id);
    }

    closeModal(id: string) {
        this.showCarousel = false;
        this.imageIsLoaded = false;
        this.modalService.close(id);
    }

    get assetIcon() {
        return this.assetsMetaData[this.assetType].icon;
    }

    selectFile() {
        this.input.nativeElement.click();
    }

    initUploader() {
        // No-op: upload logic is now handled inline via FileUploadService
        // File selection is handled by the native input element and onFileSelected
    }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) return;

        const files = Array.from(input.files);
        // Reset input so the same files can be re-selected
        input.value = '';
        files.forEach(file => this.enqueueFile(file, 'onFileSelected'));
    }

    onFileDrop(event: DragEvent) {
        if (!event.dataTransfer || !event.dataTransfer.files ||
            event.dataTransfer.files.length === 0) return;

        Array.from(event.dataTransfer.files)
            .forEach(file => this.enqueueFile(file, 'onFileDrop'));
    }

    enqueueFile(file: File, source: string) {
        this.detectFileType(file)
            .then(isAllowed => {
                if (isAllowed) {
                    console.log(source + ', file:' + file.name);
                    this.processAsset(file, this.assetType, this.ownerType);
                } else {
                    this._notificationsService.add(
                        new Notification('warn', 'File content not allowed.', '', false));
                }
            })
            .catch(error => {
                console.log('error:' + error);
            });
    }

    //this will be typically called from the parent (container)
    public uploadItem(item: CIAsset) {
        const uploadUrl = (item as any)._uploadUrl;
        const uploadFile = (item as any)._uploadFile as File;
        const uploadMethod = (item as any)._uploadMethod || 'PUT';

        if (!uploadUrl || !uploadFile) {
            console.log('Could not find upload info for \'' + item.urlName + '\'');
            const assetGroup = this.assetsArray.controls.find(f => f.value.urlName == item.urlName);
            if (assetGroup && assetGroup.get('loading')) {
                assetGroup.get('loading').patchValue(false);
            }
            this._notificationsService.add(new Notification('error', 'Failed to upload ' + item.name));
            return;
        }

        this._fileUploadService.uploadRaw(uploadFile, uploadUrl, uploadMethod).subscribe({
            next: (event) => {
                if (event.type === HttpEventType.Response) {
                    console.log('onSuccessItem, file: ' + item.urlName);
                    let assetGroup = this.assetsArray.controls.find(f => f.value.urlName == item.urlName);
                    if (!assetGroup) {
                        console.error('onSuccessItem >> no form control for urlName: ' + item.urlName);
                        return;
                    }
                    if (this.assetType === 'image') {
                        this.onImageUploadedNative(assetGroup as FormGroup, uploadUrl);
                    } else {
                        this.onDocumentUploadedNative(assetGroup as FormControl, uploadUrl);
                    }
                }
            },
            error: (err) => {
                console.error('onErrorItem, file: ' + item.urlName + ', error: ' + (err && err.message ? err.message : err));
                const assetGroup = this.assetsArray.controls.find(f => f.value.urlName == item.urlName);
                if (assetGroup && assetGroup.get('loading')) {
                    assetGroup.get('loading').patchValue(false);
                }
                this._notificationsService.add(new Notification('error', 'Failed to upload ' + item.name));
            }
        });
    }

    setDefaultItem(event) {

        //-- check if the event is a FormGrup
        if(event.valid){
            const assetControl = event;
            this.setDefaultAsset(assetControl);
        }else{
            //--get asset by ID
            const asset = this.assetsArray.controls.find(a => a.value.id === event) as FormGroup;
            this.setDefaultAsset(asset);
        }
        return false;
    }

    setDefaultCurrentItem(event) {

        if (!this.defaultAsset)
            this.defaultAsset = this.assetsArray.controls.find(c=> c.get('defaultC').value) as FormGroup;

        let newState = this.setDefaultAsset(event);
        
        if (!newState){ //when removing the state from the modal, we need to give back to the original or to the first one
            if (!this.assetsArray.controls.find(c=> c.get('defaultC').value)) //if this is nothing, then we just 'deactivate' the current default one, so, give the baton to the first control
                this.assetsArray.controls[0].get('defaultC').patchValue(true);
            else    
                this.defaultAsset.get('defaultC').patchValue(true);
        }

        return false;
    }

    removeCurrentItem(event) {
        let i = this.assetsArray.controls.findIndex(c=> c == event as FormControl);
        if (i>=0){  //index to delete
            this.promptSubscription = this._prompt.openModal(this.removeItem.bind(this), i).pipe(first()).subscribe(r=>{
                if (r >= 0){    //deleted index
                    //this.remove.next(r);
                    console.log('item #:' + r + ' was removed');
                    this.closeModal(this.imageModalId + this.asset);
                }
            });
            return false;
    
        }
    }

    removeItem(index: number) {
        //-- Get index from FormGroup to delete asset

        if (this.itemDeleted.observers.length > 0)
        {
            this.itemDeleted.emit({itemIndex:index, child:this});
        }else{
            this.executeRemoveItem(index);
        }
        return false;
    }

    public executeRemoveItem(index: number): void {
        this.removeAsset(index);
        this.assetItemForm.markAsDirty();
    }

    showItemOnReport(event) {
        //-- check if the event is a FormGrup
        if(event.valid){
            const assetControl = event;
            this.showAssetOnReport(assetControl);
        }else{
            //--get asset by ID
            const asset = this.assetsArray.controls.find(a => a.value.id === event) as FormGroup;
            this.showAssetOnReport(asset);
        }        
        return false;
    }

    downloadItem(event) {
        if (event && event.valid){
            const assetControl = event;
            this.downloadAsset(assetControl, true);
        } else {
            //--get asset by ID
            const asset = this.assetsArray && this.assetsArray.controls
                ? this.assetsArray.controls.find(a => a.value.id === event) as FormGroup
                : null;
            if (!asset) {
                console.warn('ClearanceItemAssetManagerComponent.downloadItem() >> could not find asset for event:', event);
                return false;
            }
            this.downloadAsset(asset, true);
        }
        return false;
    }

    enlargeItem(asset) {
        this.currentAssetOnDisplay = this.findControlBasedOn(asset, this.assetsArray, 'id') ;
        this.assetToDownload = asset;
        this.downloadAsset(asset);
        return false;
        //event.stopPropagation;
    }

    openCarousel(index: Number){
        if (this.modalService.modalExists('image-modal-carousel')){
            this.modalService.remove('image-modal-carousel');
        }
        this.currentIndex = index;
        this.carouselImages = this.assetsArray.value.filter(asset => asset.assetType !== undefined && asset.assetType !== null && (asset.assetType.toLowerCase() === "image" || asset.assetType.toLowerCase() === "reference"));
        if (this.carouselImages.length > 0) {
            // Force CD so *ngIf mounts <carousel> immediately (Return from annotation
            // otherwise leaves showCarousel=true until the next user click).
            this._ngZone.run(() => {
                this.showCarousel = true;
                this._cdr.detectChanges();
                setTimeout(() => {
                    if (this.modalService.modalExists('image-modal-carousel')) {
                        this.modalService.open('image-modal-carousel');
                    }
                });
            });
        }
    }

    openDocument(asset){
        const assetType = asset.value.assetType;
        if (assetType && (assetType.toLowerCase() === "image" || assetType.toLowerCase() === "reference")) {
            return;
        }
        this.currentAssetOnDisplay = this.findControlBasedOn(asset, this.assetsArray, 'id') ;
        this.assetToDownload = asset;
        this.downloadAsset(asset,undefined,true);
    }

    getSelectedAssets(): CIAsset[] {

        let images: CIAsset[] = [];

        // images = <Array<CIImage>>Object.assign({}, images, this.assetsArray.value);
        images = images.concat(this.assetsArray.value);

        return images;

    }

    processAsset(file: File, assetType: string, ownerType: string): void {
        console.log('ClearanceItemAssetManagerComponent.processAsset(\'' + file.name + '\', \'' + assetType + '\')')
        this._assetProcessing.get(assetType).processAsset(file, this.projectId, ownerType).subscribe(asset => {
            console.log('ClearanceItemAssetManagerComponent.processAsset finished, calling this.assetAdded.next() with asset:' + JSON.stringify(asset));
            this.onAssetAdded(asset);
        })
    }


    showAsset(asset: CIAsset, ownerType: string, directDownload: boolean = false, tryViewInBrowser: boolean = false ): void {
        console.log('ClearanceItemAssetManagerComponent.showAsset(\'' + asset.fullUrl + '\'')
        this.largeImageName = asset.name;
        this._assetProcessing.get(this.assetType).downloadAsset(asset, this.projectId, ownerType, directDownload, tryViewInBrowser);
    }

    addAsset(img: CIAsset): void {
        let newImg = this.initAsset(img);
        //if there is no default asset exist and current asset is supported image
        if ((this.assetsArray.controls.length <=0 || this.assetsArray.controls.findIndex(c=>c.get('defaultC').value ==true) < 0) 
                && this.supportedImages.findIndex( si=>img.fileType.includes(si))>=0) {
            newImg.get('defaultC').patchValue(true);
        }

        this.assetsArray.push(newImg);
    }

    removeImage(i: any): void {
        this.assetsArray.removeAt(i);
        this.assetItemForm.markAsDirty();
    }
    downloadAll()
    {
        if(this.assetsArray && this.assetsArray.controls.length>0)
        {
            this.assetsArray.controls.forEach(asset=>this.downloadAsset(asset as FormGroup,true));
        }
    }
    downloadAsset(asset: FormGroup, directDownload: boolean = false, tryViewInBrowser: boolean = false) {

        if (!asset) {
            console.error('ClearanceItemComponent.enlargeImage() >> control passed in \'' + asset + '\' is not of type CIImage');
            return;
        }

        const downloadingControl = asset.get('downloading');
        if (downloadingControl) {
            downloadingControl.patchValue(directDownload);
        } else {
            console.warn('ClearanceItemAssetManagerComponent.downloadAsset() >> missing downloading control for asset:', asset.value);
        }

        // let uniqueFileName = asset.get('url').value;
        // let originalFileName = asset.get('name').value;
        let assetInstance = asset.value as CIAsset;        

        this.showAsset(assetInstance, this.ownerType, directDownload, tryViewInBrowser);
    }

    setDefaultAsset(asset: FormGroup): boolean {

        let currentState = asset.get('defaultC').value;

        this.assetsArray.controls.forEach(element => {
            element.get('defaultC').patchValue(false);
        });

        asset.get('defaultC').patchValue(!currentState);

        this.assetItemForm.markAsDirty();
    
        this.defaultAssetChanged.next(asset);
        
        return !currentState;
    }


    showAssetOnReport(asset: any): void {

        if (!asset) {
            console.error('ClearanceItemComponent.enlargeImage() >> control passed in \'' + asset + '\' is not of type CIImage');
            return;
        }

        let currentState: boolean = asset.get('showOnReports').value;

        asset.get('showOnReports').patchValue(!currentState);

        asset.markAsDirty();
        this.assetItemForm.markAsDirty();
    }
    

    onImageUploaded(image: FormGroup, fileItem: any) {
        var url = new URL(fileItem.url);

        image.get('url').patchValue(url.pathname);
        image.get('loading').patchValue(false);
        image.markAsDirty();
        if (image.value.defaultC) {
            this.defaultAssetChanged.next(image);
        }
    }

    onImageUploadedNative(image: FormGroup, uploadUrl: string) {
        var url = new URL(uploadUrl);

        image.get('url').patchValue(url.pathname);
        image.get('loading').patchValue(false);
        image.markAsDirty();
        if (image.value.defaultC) {
            this.defaultAssetChanged.next(image);
        }
    }

    saveImage() {
        
        /*let img = this.img.nativeElement;
        
        // Create an empty canvas element
        var canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        // Copy the image contents to the canvas
        var ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, img.width, img.height);
        canvas.toBlob(blob => {
            saveAs(blob, this.largeImageName);
        });*/

        this.downloadAsset(this.assetToDownload, true);
        
        return false;
    }

    private findControlBasedOn(i: any, controlsArray: FormArray, property: string): FormGroup {

        if (!i) {
            console.error('ClearanceItemComponent.getValue() >> image to enlarge is ' + i);
            return;
        }

        if (!i.value || !i.value.hasOwnProperty(property) || !i.value[property]) {
            console.error('ClearanceItemComponent.getValue() >> could not identify passed in parameter: ' + i);
            return;
        }

        let currentControl = controlsArray.controls.find(c => c.get(property).value == i.value[property]);

        if (!currentControl) {
            console.error('ClearanceItemComponent.getValue() >> control passed in \'' + i + '\' is not of type formArray');
            return;
        }

        return currentControl as FormGroup;

    }

    onAssetAdded(asset: CIAsset) {

        let image = new CIImage();
        image.id = asset.name;    //use the unique id from the server, this gets overriden when the it gets saved in the backend
        image.name = asset.name; //keep the original name
        image.fullUrl = asset.fullUrl;  //asign the 64-bit encoded image for now
        image.urlName = asset.urlName;   //keep the server name 
        image.fileType = asset.fileType;
        image.loading = true;
        image.defaultC = false;
        image.showOnReports = true;
        image.assetType = this.asset;
        image.originalImageFullUrl = asset.originalImageFullUrl;
        image.mediumImageFullUrl = asset.originalImageFullUrl;

        // FileUploadService needs the signed-URL metadata from processAsset.
        // These are not CIAsset fields — copy them or uploadItem bails out.
        (image as any)._uploadUrl = (asset as any)._uploadUrl;
        (image as any)._uploadFile = (asset as any)._uploadFile;
        (image as any)._uploadMethod = (asset as any)._uploadMethod || 'PUT';

        //add the image object to the DOM and paint loading state before the PUT
        this._ngZone.run(() => {
            this.addAsset(image);
            this._cdr.detectChanges();
        });

        //execute the actual upload
        this.uploadItem(image);

    }

    removeAsset(i: number): void {
        if (i >= 0 && i < this.assetsArray.length){
            const asset = this.assetsArray.controls[i] as FormGroup;
            this.assetsArray.removeAt(i);
            if (asset.value.defaultC){
                this.defaultAssetChanged.next(asset);
            }
        }
    }

    enlargeDocument(i: any) {

        this.currentAssetOnDisplay = this.findControlBasedOn(i, this.assetsArray, 'id');

        if (!this.currentAssetOnDisplay) {
            console.error('ClearanceItemComponent.enlargeDocument() >> control passed in \'' + i + '\' is not of type CIDocument');
            return;
        }

        let uniqueFileName = this.currentAssetOnDisplay.get('urlName').value;
        let originalFileName = this.currentAssetOnDisplay.get('name').value;

        this.showAsset(uniqueFileName, this.ownerType, originalFileName);
    }
    onDocumentUploaded(doc: FormControl, fileItem: any) {
        var url = new URL(fileItem.url);

        doc.get('url').patchValue(url.pathname);
        doc.get('loading').patchValue(false);
        doc.markAsDirty();
    }

    onDocumentUploadedNative(doc: FormControl, uploadUrl: string) {
        var url = new URL(uploadUrl);

        doc.get('url').patchValue(url.pathname);
        doc.get('loading').patchValue(false);
        doc.markAsDirty();
    }

    isRemovalPrevented(): boolean{
        return !this.isClearanceUser && this.assetItemForm.controls.status
            && this.assetItemForm.controls.status.value != 'NEW'
            && this.assetItemForm.controls.status.value != 'PENDING - Updated by Production'
            && this.assetItemForm.controls.status.value != 'PENDING - Modifications Required'
            && this.assetItemForm.controls.status.value != 'PENDING - Production Info Required';
    }

    updateOriginalImageFullUrl(signedUrl){
        var parent = this;
        this.incomingAssetsArray.value.forEach(img => {
            if (img.url.replace(/^\/|\/$/g,'') === signedUrl.name.replace(/^\/|\/$/g,'')){
                img.originalImageFullUrl = signedUrl.url;
                parent.dataImages.push({
                    name: signedUrl.name,
                    url: signedUrl.url,
                    imageData: signedUrl.url
                });
            }
        });
    }

    closeCarousel($event){
        this.showCarousel = false;
    }

    detectFileType(file: File): Promise<boolean> {

        const notAllowedExtensions = ['exe', 'dll', 'js', 'msi', 'ps1', 'zip', 'rar'];
        const fileExtension = file.name.split('.').pop();
        if (!fileExtension.toLowerCase() || !(notAllowedExtensions.indexOf(fileExtension) === -1)) {
            return Promise.resolve(false); 
            }

        const fileSignatures: { [key: string]: string[] } = {
            '.exe': ['4D', '5A'],
            //'.msi': ['D0', 'CF', '11', 'E0'],
            // '.zip': ['50', '4B', '03', '04'],
            '.rar': ['52', '61', '72', '21']
            };

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = (event: ProgressEvent) => {
            const arrayBuffer = event.target && event.target['result'] ? (event.target['result'] as ArrayBuffer) : null;
        
            if (arrayBuffer) {
                const uintArray = new Uint8Array(arrayBuffer).subarray(0, 4);
                let header = '';
                for (let i = 0; i < uintArray.length; i++) {
                let ex = uintArray[i].toString(16).toUpperCase()
                ex = AppUtils.padLeft(ex,2,'0');
                header += ex;
                }
                let isAllowed = true;
                for (const [extension, signature] of Object.entries(fileSignatures)) {
                    const sig = signature.join('');
                    if (header.startsWith(sig)) 
                    {
                        isAllowed = false;
                        break;
                    }
                }
                resolve(isAllowed);
            } else {
                reject('File cannot be opened.');
            }
            };
            reader.readAsArrayBuffer(file.slice(0, 4)); 
        });
    }

    updateImageDetails(event){
        let ciImage: CIImage = event.ciImage;
        var updatedImage = this.assetsArray.controls.find((img: FormGroup) =>
            img.get('id').value == ciImage.id);
        if (updatedImage){
            updatedImage.patchValue({ notes: ciImage.notes, usage: ciImage.usage, font: ciImage.font });
        }
    }

    deleteAnnotatedImage(event: {image: CIImage, annotatedImageIndex: number}){
        var mainImage = this.assetsArray.controls.find((img: FormGroup) =>
            img.get('id').value == event.image.id);

        if (mainImage){
            mainImage.value.relatedImages.splice(event.annotatedImageIndex, 1);
        }
    }

    deleteMainImage(index: number){
        this.removeAsset(index);
    }

    ciStatusChange($event){
        this.onCIStatusChanged.emit($event);
    }
}

